import { addHours, addMinutes, format, isAfter, parseISO } from 'date-fns';

import { toZonedTime } from 'date-fns-tz';
import { timezoneAliases } from '../assets/data/timezoneAliases';
import { timezones } from '../assets/data/timezones';
import { DateFormatsEnum, DEFAULT_TIMEZONE, MIN_BLUEBIRD_DATE } from '../constants/date';
import { ITimezone, ITimezoneMetadata, IWallClock } from '../types/timezone';

export const isValidDate = (date: Date | number) => isAfter(date, MIN_BLUEBIRD_DATE);

export const formatDate = (dateString: string, dateFormat = DateFormatsEnum.MM_dd_yyyy) => {
  const utcDate = getUTCDate(dateString);

  if (isValidDate(new Date(utcDate))) {
    return format(utcDate, dateFormat);
  }

  return '';
};

export const getUTCDate = (dateString: string): Date => {
  const date = new Date(dateString);
  return addMinutes(date, date.getTimezoneOffset());
};

export const isDateInFuture = (dateString: string): boolean => {
  if (!isValidDate(new Date(dateString))) {
    return true;
  }

  return isAfter(new Date(dateString), new Date());
};

export const isDST = (): boolean => {
  const now = new Date();
  const jan = new Date(now.getFullYear(), 0, 1);
  const jul = new Date(now.getFullYear(), 6, 1);

  const stdOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
  const currentOffset = now.getTimezoneOffset();

  return currentOffset < stdOffset;
};

const WALL_CLOCK_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
};

const wallClockFormatters = new Map<string, Intl.DateTimeFormat>();

const getWallClockFormatter = (iana: string): Intl.DateTimeFormat => {
  const cached = wallClockFormatters.get(iana);

  if (cached) {
    return cached;
  }

  const formatter = new Intl.DateTimeFormat('en-US', { ...WALL_CLOCK_FORMAT, timeZone: iana });
  wallClockFormatters.set(iana, formatter);

  return formatter;
};

const readWallClock = (date: Date, iana: string): IWallClock => {
  const parts = getWallClockFormatter(iana).formatToParts(date);
  const read = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((part) => part.type === type)?.value);

  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hours: read('hour') % 24,
    minutes: read('minute'),
    seconds: read('second'),
  };
};

const wallClockAsUtcMs = (wallClock: IWallClock): number =>
  Date.UTC(wallClock.year, wallClock.month - 1, wallClock.day, wallClock.hours, wallClock.minutes, wallClock.seconds);

const MS_IN_SECOND = 1000;
const MS_IN_DAY = 24 * 60 * 60 * 1000;

export const getZoneOffsetMs = (iana: string | undefined, instant: Date): number => {
  if (!iana || Number.isNaN(instant.getTime())) {
    return NaN;
  }

  try {
    const ms = instant.getTime();
    const wholeSecondMs = ms - (((ms % MS_IN_SECOND) + MS_IN_SECOND) % MS_IN_SECOND);

    return wallClockAsUtcMs(readWallClock(instant, iana)) - wholeSecondMs;
  } catch {
    return NaN;
  }
};

export const detectUserTimezone = (): ITimezone => {
  const now = new Date();
  const locale = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const seasonProbes = [new Date(now.getFullYear(), 0, 1), new Date(now.getFullYear(), 6, 1)];
  const isAtViewerOffsetOn = (tz: ITimezone, date: Date): boolean =>
    getZoneOffsetMs(tz.utc[0], date) === -date.getTimezoneOffset() * 60 * 1000;
  // An entry is only usable if the zone its times are computed in (utc[0]) is at the viewer's offset now.
  const isAtViewerOffsetNow = (tz: ITimezone): boolean => Boolean(tz.utc[0]) && isAtViewerOffsetOn(tz, now);
  // Preferring entries that also agree on 1 Jan and 1 Jul keeps a viewer without DST off a zone that
  // observes it, and the other way round, so dates in the other season are not shown an hour off.
  const isAtViewerOffsetAllYear = (tz: ITimezone): boolean =>
    isAtViewerOffsetNow(tz) && seasonProbes.every((date) => isAtViewerOffsetOn(tz, date));
  const byLocation = timezones.filter((tz) => tz.utc.includes(locale) && isAtViewerOffsetNow(tz));

  return (
    byLocation.find((tz) => tz.utc[0] === locale) ??
    byLocation.find(isAtViewerOffsetAllYear) ??
    timezones.find(isAtViewerOffsetAllYear) ??
    byLocation[0] ??
    timezones.find(isAtViewerOffsetNow) ??
    DEFAULT_TIMEZONE
  );
};

export const findTimezoneByText = (text: string | null | undefined): ITimezone | null => {
  if (!text) {
    return null;
  }

  const label = timezoneAliases[text] ?? text;

  return timezones.find((timezone) => timezone.text === label) ?? null;
};

export const resolveStoredTimezone = (
  metadata: ITimezoneMetadata | null | undefined,
  key: keyof ITimezoneMetadata,
): ITimezone => findTimezoneByText(metadata?.[key]) ?? detectUserTimezone();

const MIN_TIME_LEAD_HOURS = 2;
const END_OF_DAY = { hours: 23, minutes: 59, seconds: 59 };

const isSameWallClockDay = (a: IWallClock, b: IWallClock): boolean =>
  a.year === b.year && a.month === b.month && a.day === b.day;

const isWallClockDay = (day: Date, wallClock: IWallClock): boolean =>
  day.getFullYear() === wallClock.year && day.getMonth() + 1 === wallClock.month && day.getDate() === wallClock.day;

export const getRoundedMinTime = (timezone: ITimezone, now: Date = new Date()): Date => {
  const today = getWallClock(now, timezone);
  const earliest = getWallClock(addHours(now, MIN_TIME_LEAD_HOURS), timezone);

  return isSameWallClockDay(today, earliest)
    ? timeOfDay(earliest.hours)
    : timeOfDay(END_OF_DAY.hours, END_OF_DAY.minutes, END_OF_DAY.seconds);
};

export const getMinTimeForSelectedDate = (
  selectedDate: Date | null,
  timezone: ITimezone,
  now: Date = new Date(),
): Date | undefined => {
  if (!selectedDate) {
    return;
  }

  return isWallClockDay(selectedDate, getWallClock(now, timezone)) ? getRoundedMinTime(timezone, now) : undefined;
};

export const getRoundedMinDate = (timezone: ITimezone, now: Date = new Date()): Date =>
  wallClockDay(getWallClock(now, timezone));

const userTimezone = detectUserTimezone();

export const formatUtcDateForDisplayInUserTimezone = (utcDate: Date | string): string => {
  const date = new Date(utcDate);

  if (isValidDate(date)) {
    const localDate = toZonedTime(date, userTimezone.utc[0]);

    const formattedDate = format(localDate, DateFormatsEnum.ddMMMyyyy);
    const formattedTime = format(localDate, DateFormatsEnum.TimeOnly);

    return `${formattedDate} ${formattedTime} (${getTimezoneLabel(userTimezone, date)})`;
  }

  return '';
};

const DST_FREE_DAY = { year: 2000, month: 0, day: 1 };

export const timeOfDay = (hours: number, minutes = 0, seconds = 0): Date =>
  new Date(DST_FREE_DAY.year, DST_FREE_DAY.month, DST_FREE_DAY.day, hours, minutes, seconds, 0);

export const TIME_REFERENCE_DATE = timeOfDay(0);

export const getWallClock = (instant: Date | string, timezone: ITimezone): IWallClock =>
  readWallClock(typeof instant === 'string' ? parseISO(instant) : instant, timezone.utc[0]);

export const wallClockDay = (wallClock: IWallClock): Date =>
  new Date(wallClock.year, wallClock.month - 1, wallClock.day);

export const wallClockOf = (day: Date, time: Date): IWallClock => ({
  year: day.getFullYear(),
  month: day.getMonth() + 1,
  day: day.getDate(),
  hours: time.getHours(),
  minutes: time.getMinutes(),
  seconds: time.getSeconds(),
});

const pad = (value: number, length = 2): string => String(value).padStart(length, '0');

export const wallClockToUtc = (wallClock: IWallClock, timezone: ITimezone): Date => {
  const iana = timezone.utc[0];
  const wallMs = wallClockAsUtcMs(wallClock);
  const readWithOffsetBefore = wallMs - getZoneOffsetMs(iana, new Date(wallMs - MS_IN_DAY));

  if (Number.isNaN(readWithOffsetBefore)) {
    return new Date(NaN);
  }

  const readWithOffsetAfter = wallMs - getZoneOffsetMs(iana, new Date(wallMs + MS_IN_DAY));
  const matches = [readWithOffsetBefore, readWithOffsetAfter].filter(
    (ms) => wallClockAsUtcMs(readWallClock(new Date(ms), iana)) === wallMs,
  );

  return new Date(matches.length ? Math.min(...matches) : readWithOffsetBefore);
};

export const isSameWallClockMinute = (a: IWallClock, b: IWallClock): boolean =>
  a.year === b.year && a.month === b.month && a.day === b.day && a.hours === b.hours && a.minutes === b.minutes;

export const resolveWallClockInstant = (
  wallClock: IWallClock,
  timezone: ITimezone,
  currentIso: string | null | undefined,
): Date => {
  if (currentIso && isValidDate(new Date(currentIso))) {
    const stored = new Date(currentIso);

    if (isSameWallClockMinute(wallClock, getWallClock(stored, timezone))) {
      return stored;
    }
  }

  return wallClockToUtc(wallClock, timezone);
};

export const isDaylightSavingAt = (iana: string | undefined, date: Date): boolean => {
  if (!iana) {
    return false;
  }

  const year = date.getUTCFullYear();
  const standardOffset = Math.min(
    getZoneOffsetMs(iana, new Date(Date.UTC(year, 0, 1))),
    getZoneOffsetMs(iana, new Date(Date.UTC(year, 6, 1))),
  );

  return getZoneOffsetMs(iana, date) > standardOffset;
};

const hasLetters = (abbr: string): boolean => /^[A-Za-z]+$/.test(abbr);

const splitUtcOffset = (offsetMs: number) => {
  const totalMinutes = Math.round(Math.abs(offsetMs) / 60000);

  return {
    isZero: totalMinutes === 0,
    sign: offsetMs < 0 ? '-' : '+',
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
  };
};

// `UTC`, `UTC-4`, `UTC+5:30`: the form `abbr` uses for zones tzdata writes as an offset.
const formatUtcOffset = (offsetMs: number): string => {
  const { isZero, sign, hours, minutes } = splitUtcOffset(offsetMs);

  if (isZero) {
    return 'UTC';
  }

  return minutes ? `UTC${sign}${hours}:${pad(minutes)}` : `UTC${sign}${hours}`;
};

const toInstantOrNow = (instant: Date | string): Date => {
  const parsed = new Date(instant);

  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

export const getTimezoneLabel = (timezone: ITimezone, instant: Date | string): string => {
  const date = toInstantOrNow(instant);

  if (hasLetters(timezone.abbr)) {
    const isDaylight = Boolean(timezone.daylightAbbr) && isDaylightSavingAt(timezone.utc[0], date);

    return isDaylight ? timezone.daylightAbbr! : timezone.abbr;
  }

  // Zones tzdata writes as an offset: compute it for the instant so DST stays right. Built from the offset
  // itself, not from Intl, which prints letters such as AST or a bare GMT for some of these zones.
  const offsetMs = getZoneOffsetMs(timezone.utc[0], date);

  return Number.isNaN(offsetMs) ? timezone.abbr : formatUtcOffset(offsetMs);
};

const TIMEZONE_TEXT_PATTERN = /^\(UTC(?:[+-]\d{2}:\d{2})?\) (.+)$/;

// `(UTC-03:00) Santiago`: the list label with the offset the zone has at the instant, so it agrees with the
// expiration field. `text` keeps the standard offset because it is the key stored in `timezone_metadata`.
export const getTimezoneDisplayText = (timezone: ITimezone, instant: Date | string): string => {
  const name = TIMEZONE_TEXT_PATTERN.exec(timezone.text)?.[1];
  const offsetMs = getZoneOffsetMs(timezone.utc[0], toInstantOrNow(instant));

  if (!name || Number.isNaN(offsetMs)) {
    return timezone.text;
  }

  const { isZero, sign, hours, minutes } = splitUtcOffset(offsetMs);

  return isZero ? `(UTC) ${name}` : `(UTC${sign}${pad(hours)}:${pad(minutes)}) ${name}`;
};

export const formatZonedDate = (
  instant: Date | string,
  timezone: ITimezone,
  dateFormat: string = DateFormatsEnum.ddMMMyyyy,
): string => {
  const date = new Date(instant);

  return isValidDate(date) ? format(wallClockDay(getWallClock(date, timezone)), dateFormat) : '';
};

export const formatZonedTime = (
  instant: Date | string,
  timezone: ITimezone,
  timeFormat: string = DateFormatsEnum.TimeOnly,
): string => {
  const date = new Date(instant);

  if (!isValidDate(date)) {
    return '';
  }

  const { hours, minutes, seconds } = getWallClock(date, timezone);

  return format(timeOfDay(hours, minutes, seconds), timeFormat);
};

export const formatZonedDateTime = (instant: Date | string, timezone: ITimezone): string => {
  const date = new Date(instant);

  if (!isValidDate(date)) {
    return '';
  }

  const day = formatZonedDate(date, timezone, DateFormatsEnum.MMMdyyyy);
  const time = formatZonedTime(date, timezone, DateFormatsEnum.HHmm);

  return `${day}, ${time} ${getTimezoneLabel(timezone, date)}`;
};
